import React, { useMemo } from 'react';
import { Sparkles, Plus, Search } from 'lucide-react';
import { ComponentHeader } from '../contract-view/ComponentHeader';
import { PersonagemCardGrid } from './PersonagemCardGrid';
import { Personagem } from '../../../types/personagem';
import { PersonagemStats } from '../../../utils/personagemStats';

interface PersonagensGridProps {
  personagens: Personagem[];
  statsById: Map<string, PersonagemStats>;
  loadingStats: boolean;
  totalCadastrados: number;
  groupByCategory: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onAddPersonagem: () => void;
  onOpenDetail: (personagem: Personagem) => void;
  onOpenImage: (imageUrl: string) => void;
  onEdit: (personagem: Personagem) => void;
  onDelete: (personagem: Personagem) => void;
  onToggleAtivo: (personagem: Personagem) => void;
}

const SEM_CATEGORIA = 'Sem categoria';

export const PersonagensGrid: React.FC<PersonagensGridProps> = ({
  personagens,
  statsById,
  loadingStats,
  totalCadastrados,
  groupByCategory,
  searchTerm,
  onSearchChange,
  onAddPersonagem,
  onOpenDetail,
  onOpenImage,
  onEdit,
  onDelete,
  onToggleAtivo
}) => {
  const groups = useMemo(() => {
    if (!groupByCategory) return null;

    const map = new Map<string, Personagem[]>();
    personagens.forEach((personagem) => {
      const key = personagem.categoria?.trim() || SEM_CATEGORIA;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(personagem);
    });

    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === SEM_CATEGORIA) return 1;
      if (b === SEM_CATEGORIA) return -1;
      return a.localeCompare(b, 'pt-BR');
    });
  }, [personagens, groupByCategory]);

  const cardGridProps = {
    statsById,
    loadingStats,
    onOpenDetail,
    onOpenImage,
    onEdit,
    onDelete,
    onToggleAtivo
  };

  return (
    <div className="w-full space-y-4">
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <ComponentHeader
          title="Personagens"
          subtitle={`${personagens.length} de ${totalCadastrados} personagem${totalCadastrados !== 1 ? 's' : ''}`}
          icon={Sparkles}
        >
          <div className="flex items-center gap-3 mt-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/70" />
              <input
                type="text"
                placeholder="Buscar personagens..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50"
              />
            </div>

            <button
              onClick={onAddPersonagem}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors flex items-center gap-2 border border-white/30"
            >
              <Plus className="w-4 h-4" />
              Novo Personagem
            </button>
          </div>
        </ComponentHeader>

        <div className="p-4">
          {personagens.length === 0 ? (
            <div className="text-center py-8">
              <Sparkles className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500 mb-4">
                {totalCadastrados === 0
                  ? 'Nenhum personagem cadastrado ainda.'
                  : 'Nenhum personagem encontrado com os filtros atuais.'}
              </p>
              <button
                onClick={onAddPersonagem}
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Cadastrar Personagem
              </button>
            </div>
          ) : groups ? (
            <div className="space-y-6">
              {groups.map(([categoria, itens]) => (
                <div key={categoria}>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-sm font-bold text-slate-700">{categoria}</h3>
                    <span className="text-xs text-slate-400">
                      ({itens.length} personagem{itens.length !== 1 ? 's' : ''})
                    </span>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>
                  <PersonagemCardGrid personagens={itens} {...cardGridProps} />
                </div>
              ))}
            </div>
          ) : (
            <PersonagemCardGrid personagens={personagens} {...cardGridProps} />
          )}
        </div>
      </div>
    </div>
  );
};
